import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { IVTableCell } from "@app/directives/_layout/v-table/_models/IVTableCell";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { DateRange } from "@app/enum/DateRange.enum";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { WindowBreakpoint } from "@services/responsive.service";
import { RouteVisitStatus } from "@tms/_enums/RouteVisitStatus";
import { IPlannedRouteVisit } from "@tms/_models/IPlannedRouteVisit";
import { IPlannedRouteDetail, PlannedRouteService, selectMany } from "@tms/_services/planned-route.service";
import { lastValueFrom, Subscription } from "rxjs";
import { DateService, DateUnit } from "../../../../../services/date.service";
import { IEarlyLatePerformanceInterface } from "../../_models/IEarlyLatePerformanceInterface";
import { IPlannedRouteOptions } from "../../_models/IPlannedRouteOptions";
import { PlannedRouteService2 } from "../../_services/planned-route.service2";
import { AheadBehindComponent } from "./ahead-behind/ahead-behind.component";
import "./visit-performance.component.scss";

export interface IPlannedRouteVisitPerformance extends IPlannedRouteVisit, IEarlyLatePerformanceInterface {
}

export interface IVisitPerformance extends IEarlyLatePerformanceInterface {
    Id?: number;
    name: string;

    totalVisitCount: number;
    missCount: number;
    successCount: number;

    arrivalEarlyCount: number;
    arrivalLateCount: number;

    departureEarlyCount: number;
    departureLateCount: number;

    performanceRating?: number;

    visits: IPlannedRouteVisitPerformance[];
}

@Component({
    selector: "visit-performance",
    template: require("./visit-performance.component.html")
})
export class VisitPerformanceComponent implements OnInit, OnDestroy {

    defaultDateRange = DateRange.Last7Days;
    filterEvent: IAggregateFilterEvent<IPlannedRouteVisitPerformance>;

    routes: IPlannedRouteDetail[];
    visits: IPlannedRouteVisit[];

    visitPerformance: IVisitPerformance[] = [];
    selectedRow: IVisitPerformance;

    maxVisits = 5;
    maxDiffMins = 60;

    loading = false;
    plannedRoutesSubscription: Subscription;

    WindowBreakpoint = WindowBreakpoint;

    tableColumns: IVTableColumn<IVisitPerformance>[];
    tableRows: IVTableRow[];

    selectedVisitsTableColumns: IVTableColumn<IPlannedRouteVisitPerformance>[];
    selectedVisitsTableRows: IVTableRow[];

    constructor(
        private readonly plannedRouteService: PlannedRouteService,
        private readonly plannedRouteService2: PlannedRouteService2,
        private readonly dateService: DateService,
        private readonly vTableService: VTableService) {
    }

    ngOnInit() {
        this.prepareForData();
    }

    ngOnDestroy(): void {
        this.plannedRoutesSubscription?.unsubscribe();
    }

    rowClick($event: IVTableRow) {
        this.selectedRow = $event.data;
        this.createSelectedVisitsTableRows();
    }

    onVisitRowClicked($event: IVTableRow) {
        this.plannedRouteService.openRouteDetailsDialogById($event.data.PlannedRouteId, 'app.tms.visitperformance'/* TmsModule.RoutePerformance*/);
    }

    onFilterChanged(event: IAggregateFilterEvent<IPlannedRouteVisitPerformance>) {
        if (!event.trigger) {
            return;
        }

        this.filterEvent = event;

        if (event.trigger === "date") {
            this.requestData(true);
        }
    }

    requestData(invalidateCache = false): void {
        this.loading = true;

        const options: IPlannedRouteOptions = {
            invalidateCache: invalidateCache,
            formatPlannedVisitsForDisplay: true,
            startedRoutesOnly: true
        };

        lastValueFrom(this.plannedRouteService2.requestPlannedRoutes(this.filterEvent.date[0], this.filterEvent.date[1], options)).finally(() => {
            this.createTableRows();
        });
    }
    // ---


    // Success Rate - aka visit rate

    // Average Arrival Delta
    // Max Arrival Delta

    // Average On Site Delta
    // Max On Site Delta

    // Route performance? e.g. what is success rate of routes that include this stop

    private prepareForData(): void {
        this.createTable();
        this.createSelectedVisitsTable();
        this.plannedRoutesSubscription = this.plannedRouteService2.plannedRoutesSubject.subscribe((routes) => {
            this.routes = routes;
            this.visits = selectMany(this.routes, route => route.PlannedVisits);

            this.processVisits();
            this.createTableRows();
            this.loading = false;
        });
    }

    private processVisits() {
        this.visitPerformance = [];

        this.visits.forEach((visit, index) => {

            // If we are planned, ignore
            if (visit.Status == RouteVisitStatus.Planned) {
                return;
            }

            let existingVP = this.visitPerformance.find(p => p.name == visit.Name);

            if (!existingVP) {
                existingVP = {
                    Id: index,
                    name: visit.Name,

                    totalVisitCount: 0,
                    missCount: 0,
                    successCount: 0,

                    arrivalEarlyCount: 0,
                    arrivalLateCount: 0,

                    departureEarlyCount: 0,
                    departureLateCount: 0,

                    arrivalEarlyMins: 0,
                    arrivalLateMins: 0,

                    onsiteEarlyMins: 0,
                    onsiteLateMins: 0,

                    visits: []
                };

                this.visitPerformance.push(existingVP);
            }

            switch (visit.Status) {
                case RouteVisitStatus.Arrived:
                case RouteVisitStatus.Departed:
                    this.processDepartedVisit(existingVP, visit);

                    let visitperformance = this.createPerformanceVisit(visit);
                    existingVP.visits.push(visitperformance);

                    break;

                case RouteVisitStatus.Missed:

                    existingVP.missCount++;
                    break;
            }





            existingVP.totalVisitCount = existingVP.successCount + existingVP.missCount;
            existingVP.performanceRating = existingVP.totalVisitCount
                ? existingVP.successCount / existingVP.totalVisitCount
                : 0;

            this.maxDiffMins = Math.max(this.maxDiffMins, existingVP.arrivalEarlyMins);
            this.maxDiffMins = Math.max(this.maxDiffMins, existingVP.arrivalLateMins);
        });
    }

    private createPerformanceVisit(visit: IPlannedRouteVisit): IPlannedRouteVisitPerformance {
        let vp: IPlannedRouteVisitPerformance = visit as any;

        vp.arrivalEarlyMins = 0;
        vp.arrivalLateMins = 0;
        vp.onsiteEarlyMins = 0;
        vp.onsiteLateMins = 0;

        this.calculateEarlyLateMins(vp, visit);

        return vp;
    }

    private processDepartedVisit(existingVP: IVisitPerformance, visit: IPlannedRouteVisit) {
        existingVP.successCount++;

        this.calculateEarlyLateMins(existingVP, visit);

        // Arrival / departure
        if (existingVP.arrivalEarlyMins) {
            existingVP.arrivalEarlyCount++;
        }
        else {
            existingVP.arrivalLateCount++;
        }

        // On site time
        if (existingVP.onsiteEarlyMins > 0) {
            existingVP.departureEarlyCount++;
        }
        else {
            existingVP.departureLateCount++;
        }
    }

    private calculateEarlyLateMins(existingVP: IEarlyLatePerformanceInterface, visit: IPlannedRouteVisit) {

        let arrivalDiff = visit.ActualArrivalTime ? this.dateService.getDiff(visit.ActualArrivalTime, visit.PlannedArrivalTime, DateUnit.Min) : 0;
        let departureDiff = visit.ActualDepartureTime ? this.dateService.getDiff(visit.ActualDepartureTime, visit.PlannedDepartureTime, DateUnit.Min) : 0;

        // Arrival / departure
        if (arrivalDiff > 0) {
            existingVP.arrivalEarlyMins += arrivalDiff;
        }
        else {
            existingVP.arrivalLateMins -= arrivalDiff;
        }

        // On site time
        if (departureDiff > 0) {
            existingVP.onsiteEarlyMins += departureDiff;
        }
        else {
            existingVP.onsiteLateMins -= departureDiff;
        }
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "VisitPerformance.VisitName" }),
                createCellFunction: performance => this.vTableService.createTableCell(performance.name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "VisitPerformance.PlanCount", width: 150, sorted: true, sortDirection: SortDirection.Ascending, sortType: SortType.Number }),
                createCellFunction: performance => this.vTableService.createTableCell(performance.totalVisitCount)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "VisitPerformance.SuccessCount", width: 160 }),
                createCellFunction: performance => this.vTableService.createTableCell(performance.successCount)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "VisitPerformance.VisitRate", width: 150, searchable: false }),
                createCellFunction: performance => this.createPerformanceRatingCell(performance)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "VisitPerformance.ArrivalTime", width: 150, sortable: false, searchable: false, excludeFromExport: true }),
                createCellFunction: performance => this.vTableService.createTableCell(performance, { component: AheadBehindComponent, componentInputs: { left: Math.round(performance.arrivalEarlyMins), right: Math.round(performance.arrivalLateMins), max: this.maxDiffMins } })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "VisitPerformance.OnSiteDuration", width: 150, sortable: false, searchable: false, excludeFromExport: true }),
                createCellFunction: performance => this.vTableService.createTableCell(performance, { component: AheadBehindComponent, componentInputs: { left: Math.round(performance.onsiteEarlyMins), right: Math.round(performance.onsiteLateMins), max: this.maxDiffMins } })
            },
        ]
    }

    private createSelectedVisitsTable(): void {
        this.selectedVisitsTableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Name" }),
                createCellFunction: visit => this.vTableService.createTableCell(visit.Name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "VisitPerformance.ArrivalTime", width: 150, sortable: false, searchable: false, excludeFromExport: true }),
                createCellFunction: visit => this.vTableService.createTableCell(performance, { component: AheadBehindComponent, componentInputs: { left: Math.round(visit.arrivalEarlyMins), right: Math.round(visit.arrivalLateMins), max: this.maxDiffMins } })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "VisitPerformance.OnSiteDuration", width: 150, sortable: false, searchable: false, excludeFromExport: true }),
                createCellFunction: visit => this.vTableService.createTableCell(performance, { component: AheadBehindComponent, componentInputs: { left: Math.round(visit.onsiteEarlyMins), right: Math.round(visit.onsiteLateMins), max: this.maxDiffMins } })
            },
        ]
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.visitPerformance, this.tableColumns);
    }

    private createSelectedVisitsTableRows(): void {
        if (!this.selectedRow) {
            return;
        }
        this.selectedVisitsTableRows = this.vTableService.createTableRows(this.selectedRow.visits, this.selectedVisitsTableColumns);
    }

    private createPerformanceRatingCell(performance: IVisitPerformance): IVTableCell {
        const percentage = performance.performanceRating * 100;
        return this.vTableService.createTableCell(`${Math.round(percentage)}%`, { sortData: percentage });
    }
}
